import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Tag, GripVertical, Pencil, Trash2, Palette, Check, X, Plus } from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDel } from '../../../lib/api.js';
import { linkedDrip, canLink, needsDispositionTrigger, triggersWith, triggersWithout } from '../../../lib/dripAction.js';

const DEFAULT_COLOR = '#8b5cf6';
const errText = (e, fb) => e?.response?.data?.error || e?.message || fb;

/** Point `chosenId` (or nothing) at this disposition: unlink the old drip, link the new one. */
export async function syncDripLink(drips, before, after, chosenId) {
  const prev = before ? linkedDrip(drips, before) : null;
  if (prev && String(prev.dripGroupId) !== String(chosenId || '')) {
    await apiPut(`/marketing/drips/${prev.dripGroupId}`, { dispositionTriggers: triggersWithout(prev, before) });
  }
  const next = drips.find((d) => String(d.dripGroupId) === String(chosenId || ''));
  if (!next) return;
  const base = before ? triggersWithout(next, before) : (next.dispositionTriggers || []);
  const triggers = triggersWith({ dispositionTriggers: base }, after);
  const setTrigger = needsDispositionTrigger(next);
  const same = !setTrigger && triggers.length === (next.dispositionTriggers || []).length
    && triggers.every((t, i) => t === next.dispositionTriggers[i]);
  if (!same) await apiPut(`/marketing/drips/${next.dripGroupId}`, { ...(setTrigger ? { trigger: 'disposition_change' } : {}), dispositionTriggers: triggers });
}

function DripSelect({ drips, value, onChange, id }) {
  return (
    <select id={id} className="input w-full text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid="disposition-drip-select">
      <option value="">No Drip Action</option>
      {drips.map((d) => (
        <option key={d.dripGroupId} value={d.dripGroupId} disabled={!canLink(d)}>{d.name}</option>
      ))}
    </select>
  );
}

function ColorField({ value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-civix-purple text-white text-xs font-bold cursor-pointer">
        <Palette className="w-3.5 h-3.5" /> PICK COLOR
        <input type="color" className="sr-only" value={value || DEFAULT_COLOR} onChange={(e) => onChange(e.target.value)} data-testid="disposition-color" />
      </label>
      <span className="w-5 h-5 rounded-full border border-white/20" style={{ background: value || 'transparent' }} aria-label="Selected color" />
      {value && <button type="button" onClick={() => onChange('')} className="text-xs text-white/50 hover:text-white underline">No color</button>}
    </div>
  );
}

function Row({ d, drips, editing, onEdit, onCancel, onSave, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: d._id });
  const current = linkedDrip(drips, d);
  const [draft, setDraft] = useState({ label: d.label, color: d.color || '', dripId: current ? String(current.dripGroupId) : '' });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <li ref={setNodeRef} style={style} className="border-t border-white/5 py-2.5" data-testid={`disposition-row-${d._id}`}>
      {editing ? (
        <div className="space-y-2 px-1">
          <input className="input w-full text-sm" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={64} aria-label="Tag name" />
          <ColorField value={draft.color} onChange={(c) => setDraft({ ...draft, color: c })} />
          <DripSelect drips={drips} value={draft.dripId} onChange={(v) => setDraft({ ...draft, dripId: v })} />
          <div className="flex gap-2">
            <button type="button" className="btn-primary text-xs inline-flex items-center gap-1" onClick={() => onSave(d, draft)}><Check className="w-3.5 h-3.5" /> Save</button>
            <button type="button" className="btn-ghost text-xs inline-flex items-center gap-1" onClick={onCancel}><X className="w-3.5 h-3.5" /> Cancel</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-[24px_1fr_28px_1fr_auto] items-center gap-2 text-sm">
          <button type="button" {...attributes} {...listeners} aria-label="Drag to reorder" className="text-white/30 hover:text-white/60 cursor-grab"><GripVertical className="w-4 h-4" /></button>
          <span className="truncate">{d.label}</span>
          <span className="w-5 h-5 rounded border border-white/15" style={{ background: d.color || 'transparent' }} />
          <span className="truncate text-white/60">{current?.name || <span className="text-white/25">—</span>}</span>
          <span className="flex gap-1">
            <button type="button" aria-label={`Edit ${d.label}`} onClick={() => onEdit(d._id)} className="p-1.5 rounded text-civix-purpleSoft hover:bg-white/5"><Pencil className="w-4 h-4" /></button>
            <button type="button" aria-label={`Delete ${d.label}`} onClick={() => onDelete(d)} className="p-1.5 rounded text-rose-400 hover:bg-rose-500/10"><Trash2 className="w-4 h-4" /></button>
          </span>
        </div>
      )}
    </li>
  );
}

/**
 * Marketing Hub → Dispositions: create, edit, reorder and delete disposition tags, each with an
 * optional Drip Action (the drip that starts whenever the tag is applied, one lead or many).
 * @returns {JSX.Element}
 */
export default function DispositionsPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['dispositions'], queryFn: () => apiGet('/dispositions') });
  const { data: dripData } = useQuery({ queryKey: ['marketing-drips'], queryFn: () => apiGet('/marketing/drips') });
  const items = data?.dispositions || [];
  const drips = dripData?.drips || [];
  const [form, setForm] = useState({ label: '', color: '', dripId: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const refresh = () => { qc.invalidateQueries({ queryKey: ['dispositions'] }); qc.invalidateQueries({ queryKey: ['marketing-drips'] }); };

  async function create() {
    const label = form.label.trim();
    if (!label) return;
    setSaving(true);
    try {
      const r = await apiPost('/dispositions', { label, ...(form.color ? { color: form.color } : {}) });
      if (form.dripId) await syncDripLink(drips, null, r.disposition, form.dripId);
      toast.success(r.restored ? `“${label}” restored` : 'Disposition tag created');
      setForm({ label: '', color: '', dripId: '' });
      refresh();
    } catch (e) {
      toast.error(errText(e, 'Could not create the tag'));
    }
    setSaving(false);
  }

  async function save(d, draft) {
    const label = draft.label.trim();
    if (!label) { toast.error('Name is required'); return; }
    try {
      const r = await apiPut(`/dispositions/${d._id}`, { label, color: draft.color || DEFAULT_COLOR });
      await syncDripLink(drips, d, r.disposition, draft.dripId);
      toast.success('Saved');
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(errText(e, 'Could not save the tag'));
    }
  }

  async function remove(d) {
    if (!window.confirm(`Delete the tag “${d.label}”? Leads keep their history; the tag stops appearing in menus.`)) return;
    try {
      const linked = linkedDrip(drips, d);
      if (linked) await apiPut(`/marketing/drips/${linked.dripGroupId}`, { dispositionTriggers: triggersWithout(linked, d) });
      await apiDel(`/dispositions/${d._id}`);
      toast.success('Tag deleted');
      refresh();
    } catch (e) {
      toast.error(errText(e, 'Could not delete the tag'));
    }
  }

  async function onDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((d) => d._id === active.id);
    const to = items.findIndex((d) => d._id === over.id);
    const next = arrayMove(items, from, to);
    qc.setQueryData(['dispositions'], { ...(data || {}), dispositions: next });
    try {
      await Promise.all(next.map((d, i) => ((d.sortOrder ?? 0) !== (i + 1) * 10 ? apiPut(`/dispositions/${d._id}`, { sortOrder: (i + 1) * 10 }) : null)));
    } catch (e) {
      toast.error(errText(e, 'Could not save the new order'));
    }
    refresh();
  }

  return (
    <div className="panel p-5 space-y-5 max-w-3xl" data-testid="dispositions-panel">
      <div>
        <div className="font-display font-bold text-lg flex items-center gap-2"><Tag className="w-4 h-4 text-civix-purple" /> Disposition Tags</div>
        <p className="text-sm text-white/55 mt-1">Create disposition tags so you can assign them to your leads later.</p>
      </div>

      <div className="space-y-3">
        <input
          className="input w-full text-sm" placeholder="Disposition tag name" value={form.label} maxLength={64}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
          data-testid="disposition-name"
        />
        <div>
          <div className="text-sm text-white/80">Background color <span className="text-xs text-white/40">(optional)</span></div>
          <p className="text-xs text-white/45 mb-1.5">Color leads with this tag so they stand out when viewing all your leads. Leave this alone for no color.</p>
          <ColorField value={form.color} onChange={(c) => setForm({ ...form, color: c })} />
        </div>
        <div>
          <label htmlFor="new-drip-action" className="text-sm text-white/80">Drip Action <span className="text-xs text-white/40">(optional)</span></label>
          <p className="text-xs text-white/45 mb-1.5">Start a drip immediately after dispositioning a lead.</p>
          <DripSelect id="new-drip-action" drips={drips} value={form.dripId} onChange={(v) => setForm({ ...form, dripId: v })} />
        </div>
        <button type="button" onClick={create} disabled={saving || !form.label.trim()} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="disposition-save">
          <Plus className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div>
        <div className="text-sm font-semibold mb-2">Current disposition tags</div>
        <div className="grid grid-cols-[24px_1fr_28px_1fr_auto] gap-2 text-[11px] uppercase tracking-wide text-white/35 px-1 pb-1">
          <span /><span>Tag</span><span /><span>Drip Action</span><span className="w-16" />
        </div>
        {isLoading ? <div className="text-white/50 text-sm">Loading…</div> : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((d) => d._id)} strategy={verticalListSortingStrategy}>
              <ul>
                {items.map((d) => (
                  <Row key={d._id} d={d} drips={drips} editing={editingId === d._id} onEdit={setEditingId} onCancel={() => setEditingId(null)} onSave={save} onDelete={remove} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
        {!isLoading && items.length === 0 && <p className="text-sm text-white/45 py-4">No tags yet — create your first one above.</p>}
      </div>
    </div>
  );
}
